import React, { useRef, useState } from "react";
import { Container, Row, Col, Button, Form } from "react-bootstrap";

// --- INLINE ERP UTILITIES ---
const generateOrderNo = ({ prefix = "PO", category = "PROC", orderId = "" }) => {
  const formattedCat = category ? category.substring(0, 3).toUpperCase() : "PROC";
  const idPart = orderId || "10045";
  return `${prefix}-${formattedCat}-${idPart}`;
};

const getFulfillmentTimeline = (category, financialYear) => {
  return "14 - 30 Business Days";
};

const getVendorDetails = (vendorType) => {
  return {
    prefixTitle: "M/s.",
    representativeTitle: "its authorized representative,",
  };
};

// --- DEMO DATA DEFAULT ---
const defaultVendorOrder = {
  companyName: "Acme Industrial Supplies Pvt Ltd",
  contactPerson: "Robert Vance",
  itemDescription: "Precision CNC Machine Components & Tooling Assemblies",
  financialYear: "FY 2026-27",
  orderId: "89420",
  itemCategory: "Capital Machinery",
  vendorType: "corporate",
};

export default function PurchaseOrderLetter({ vendorOrder = defaultVendorOrder }) {
  const printRef = useRef();

  const { prefixTitle, representativeTitle } = getVendorDetails(
    vendorOrder?.vendorType
  );

  const [formData, setFormData] = useState({
    refNo: generateOrderNo({
      prefix: "PO",
      category: vendorOrder?.itemCategory,
      orderId: vendorOrder?.orderId,
    }),

    date: new Date().toLocaleDateString("en-IN"),

    vendorName: vendorOrder?.companyName || "",
    contactPerson: vendorOrder?.contactPerson || "",
    itemDescription: vendorOrder?.itemDescription || "",
    financialYear: vendorOrder?.financialYear || "FY 2026-27",

    deliveryTimeline: getFulfillmentTimeline(
      vendorOrder?.itemCategory || "",
      vendorOrder?.financialYear || ""
    ),

    purchaseOrderId: vendorOrder?.orderId || "",

    contractFramework: "Master Services & Procurement Agreement",
    complianceStandard: "ISO 9001:2015 Quality & Regulatory Standards",

    companyName: "GLOBAL LOGISTICS & MANUFACTURING CORP",
    address: "Bangalore Industrial Area, Karnataka",
  });

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handlePrint = () => {
    const content = printRef.current.innerHTML;
    const win = window.open("", "", "width=900,height=700");

    win.document.write(`
      <html>
        <head>
          <title>Purchase Order Letter</title>
          <style>
            body {
              font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
              padding: 120px 40px 40px;
              line-height: 1.8;
              color: #222;
            }
            .center { text-align: center; }
            .right { text-align: right; }
            .bold { font-weight: bold; }
          </style>
        </head>
        <body>${content}</body>
      </html>
    `);

    win.document.close();
    win.print();
  };

  return (
    <Container className="mt-4">
      {/* FORM INPUTS */}
      <Form className="mb-4">
        <Row className="mb-2">
          <Col>
            <Form.Control
              name="refNo"
              value={formData.refNo}
              onChange={handleChange}
              placeholder="Ref / PO Number"
            />
          </Col>
          <Col>
            <Form.Control
              name="date"
              value={formData.date}
              onChange={handleChange}
              placeholder="Issue Date"
            />
          </Col>
        </Row>

        <Row className="mb-2">
          <Col>
            <Form.Control
              name="vendorName"
              value={formData.vendorName}
              onChange={handleChange}
              placeholder="Vendor / Supplier Name"
            />
          </Col>
          <Col>
            <Form.Control
              name="contactPerson"
              value={formData.contactPerson}
              onChange={handleChange}
              placeholder="Contact Person / Representative"
            />
          </Col>
        </Row>

        <Row className="mb-2">
          <Col>
            <Form.Control
              name="itemDescription"
              value={formData.itemDescription}
              onChange={handleChange}
              placeholder="Item / Services Scope"
            />
          </Col>
          <Col>
            <Form.Control
              name="financialYear"
              value={formData.financialYear}
              onChange={handleChange}
              placeholder="Financial Period"
            />
          </Col>
        </Row>

        <Row className="mb-2">
          <Col>
            <Form.Control
              name="deliveryTimeline"
              value={formData.deliveryTimeline}
              onChange={handleChange}
              placeholder="Delivery Timeline"
            />
          </Col>
          <Col>
            <Form.Control
              name="purchaseOrderId"
              value={formData.purchaseOrderId}
              onChange={handleChange}
              placeholder="Purchase Order ID"
            />
          </Col>
        </Row>
      </Form>

      <div className="text-end mb-3">
        <Button onClick={handlePrint} variant="primary">
          Print Confirmation Letter
        </Button>
      </div>

      {/* PRINT AREA */}
      <div ref={printRef} style={{ padding: 25, border: "1px solid #000" }}>
        {/* REF + DATE */}
        <div style={{ marginTop: "140px" }}>
          <Row className="mt-3">
            <Col>
              <strong>Ref No: {formData.refNo}</strong>
            </Col>
            <Col className="right">
              <strong>Date: {formData.date}</strong>
            </Col>
          </Row>
        </div>

        {/* TITLE */}
        <Row className="mt-4">
          <Col className="center">
            <h4>
              <u>PURCHASE ORDER CONFIRMATION</u>
            </h4>
          </Col>
        </Row>

        {/* BODY */}
        <div className="mt-4">
          <p>
            This letter serves as formal confirmation that vendor{" "}
            <strong>
              {prefixTitle} {formData.vendorName}
            </strong>
            , represented by {representativeTitle}{" "}
            <strong>{formData.contactPerson}</strong>, has been awarded the
            procurement order for <strong>{formData.itemDescription}</strong>{" "}
            for the financial period <strong>{formData.financialYear}</strong>.
          </p>

          <p>
            The agreed timeline for delivery/execution is{" "}
            <strong>{formData.deliveryTimeline}</strong>. The registered Purchase
            Order tracking ID for this transaction is{" "}
            <strong>{formData.purchaseOrderId}</strong>.
          </p>

          <p>
            This order is bound under the company's{" "}
            <strong>{formData.contractFramework}</strong> and governed by{" "}
            <strong>{formData.complianceStandard}</strong>.
          </p>
        </div>

        {/* FOOTER */}
        <Row className="mt-5" style={{ paddingTop: "40px" }}>
          <Col className="text-end">
            <strong>AUTHORIZED SIGNATORY</strong>
            <div>Procurement & Supply Chain Dept</div>
          </Col>
        </Row>
      </div>
    </Container>
  );
}